import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// @mui/material components
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { makeStyles, withStyles } from '@mui/styles';

// material ui icons
import LocationIcon from '@mui/icons-material/Store';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

// Redux
import {
  useCreateLocationMutation,
  useGetLocationsQuery,
  useUpdateLocationMutation,
  useLazyGetCashdrawersQuery,
  useGetWorkstationsQuery,
} from 'api/location.api';
import {
  useGetParameterListsQuery,
  useGetTemplateSetsQuery,
} from 'api/parameter.api';
import {
  useAvailableLanguages,
  getTenantSupportedLanguages,
  isLocaleOutsideSupportedLanguages,
  resolveEnvDefaultLocale,
} from 'hooks/useAvailableLanguages';
import { useGetReferenceListsQuery } from 'api/reference-list.api';
import { useGetTenantQuery } from 'api/tenant.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import { selectLocale } from '../../features/session/sessionSlice';
import { PromptAlert } from 'features/ux/RouterPrompt';

const useStyles = makeStyles(styles);
let pendingRedirectUrl = null;

export default function Location(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const baseLocale = useSelector(selectLocale);
  const { data: tenant } = useGetTenantQuery();
  const { data: parameterSets = [] } = useGetParameterListsQuery();
  const { data: referenceLists = [] } = useGetReferenceListsQuery();
  const country =
    tenant?.base_locale?.substring(tenant?.base_locale?.length - 2) || '';
  const locationTypes = referenceLists.find(
    (loctype) => loctype.name === 'locationtype'
  );
  const [currentLocationId] = useState(location?.state?.location?.key);
  const { data: workstations, refetch: refetchWorkstations } =
    useGetWorkstationsQuery(currentLocationId);
  const { data: templateSets = [] } = useGetTemplateSetsQuery();
  const baseLanguages = useAvailableLanguages();
  const envSupportedLanguages =
    getTenantSupportedLanguages(tenant) ?? baseLanguages;
  const { data: locationsData } = useGetLocationsQuery({ limit: 20 });
  const locations = locationsData?.results || [];

  const viewIsDirty = useSelector(selectViewIsDirty);
  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();
  const [getCashdrawers] = useLazyGetCashdrawersQuery();
  const [cashdrawers, setCashdrawers] = useState([]);

  // Single source of truth for what may be assigned as the store safe: a cash
  // drawer flagged safe-eligible in the colleague app, or an active physical
  // safe. The dropdown, the Active-status gate and the save validation all read
  // this so they can no longer disagree with one another.
  const eligibleSafes = useMemo(
    () =>
      (cashdrawers ?? []).filter(
        (drawer) =>
          drawer.safe_eligible === true ||
          (drawer.type === 'Safe' && drawer.status === 'Active')
      ),
    [cashdrawers]
  );

  // Whether a safe_id can still be resolved to one of the registered devices.
  // An unassigned 'default' sentinel, or a device that has gone away, cannot.
  const isRegisteredSafe = useCallback(
    (safeId) =>
      Boolean(safeId) &&
      eligibleSafes.some((drawer) => drawer.cashdrawer_id === safeId),
    [eligibleSafes]
  );

  const getEmptyLocation = () => {
    let location = {
      location_id: '',
      location_name: '',
      type: '',
      safe: '',
      card_payment_provider: 'None',
      card_processor_name: '',
      gift_card_processor: 'None',
      gift_card_processor_name: '',
      wallet_processor: 'None',
      wallet_processor_name: '',
      poa_processor: 'None',
      poa_processor_name: '',
      status: 'pending',
      locale: resolveEnvDefaultLocale(tenant, baseLocale),
    };
    return location;
  };

  const canLocationBeActivated = () => {
    if (
      cashdrawers?.filter(
        (drawer) => drawer.type === 'Cashdrawer' && drawer.status === 'Active'
      ).length > 0 &&
      workstations?.filter((workstation) => workstation.status === 'Active')
        .length > 0 &&
      eligibleSafes.length > 0 &&
      safe &&
      safeState !== 'error'
    )
      return true;
    else return false;
  };

  const getInitialLocationType = () => {
    if (!locationTypes) return '';
    if (locationTypes.default_type === 'default')
      // eslint-disable-next-line no-eval -- pre-existing; default_value shape in live reference lists is unverified
      return locationTypes.list[eval(locationTypes.default_value) - 1]
        .entry_code;
    else if (locationTypes.default_type === 'first')
      return locationTypes.list[0].entry_code;
    else return '';
  };

  let newLocation = location?.state?.new || false;
  let currentLocation = newLocation
    ? getEmptyLocation()
    : location?.state?.location || getEmptyLocation();

  const originalLocationId = currentLocation.key;
  const originalLocationName = currentLocation.name;
  const classes = useStyles();

  const [locId, setLocId] = useState(currentLocation.key);
  const [locIdState, setLocIdState] = useState('');
  const [locIdError, setLocIdError] = useState('');
  const [name, setName] = useState(currentLocation.name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState(currentLocation.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [locationType, setLocationType] = useState(
    newLocation ? getInitialLocationType() : currentLocation.locationType
  );
  const [locationTypeState, setLocationTypeState] = useState('');
  const [locale, setLocale] = useState(currentLocation.locale);
  const [localeState, setLocaleState] = useState('');
  const [showLocaleDriftAlert, setShowLocaleDriftAlert] = useState(false);
  // Latches the on-open auto-prompt so a tenant refetch (a new supported_languages
  // reference) does not re-pop the modal after dismissal; the save-time guard
  // still re-checks independently.
  const localeDriftPromptedRef = useRef(false);
  // Payment-processor selection UI removed for the PaymentV2 migration.
  // Existing values are preserved on save via pass-through from the loaded location
  const [safe, setSafe] = useState(currentLocation.safeId);
  const [safeState, setSafeState] = useState('');
  const [status, setStatus] = useState(currentLocation.status);
  const [statusState, setStatusState] = useState('');
  const [addressLine1, setAddressLine1] = useState(
    currentLocation.addressLine1
  );
  const [addressLine1State, setAddressLine1State] = useState('');
  const [addressLine1Error, setAddressLine1Error] = useState('');
  const [addressLine2, setAddressLine2] = useState(
    currentLocation.addressLine2
  );
  const [addressLine2State, setAddressLine2State] = useState('');
  const [addressLine2Error, setAddressLine2Error] = useState('');
  const [city, setCity] = useState(currentLocation.city);
  const [cityState, setCityState] = useState('');
  const [cityError, setCityError] = useState('');
  const [state, setState] = useState(
    currentLocation.state ? currentLocation.state : ''
  );
  const [stateState, setStateState] = useState('');
  const [stateError, setStateError] = useState('');
  const [postalCode, setPostalCode] = useState(currentLocation.postalCode);
  const [postalCodeState, setPostalCodeState] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [telephone, setTelephone] = useState(currentLocation.phone);
  const [telephoneState, setTelephoneState] = useState('');
  const [telephoneError, setTelephoneError] = useState('');
  const [parameterSet, setParameterSet] = useState(currentLocation.parameters);
  const [parameterSetState, setParameterSetState] = useState('');
  const [parameterSetError, setParameterSetError] = useState('');
  const [templateSet, setTemplateSet] = useState(currentLocation.templateName);
  const [templateSetState, setTemplateSetState] = useState('');
  const [templateSetError, setTemplateSetError] = useState('');
  const [taxId, setTaxId] = useState(currentLocation?.taxId ?? '');
  const [taxIdState, setTaxIdState] = useState('');
  const [taxIdError, setTaxIdError] = useState('');

  const now = new Date();
  const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  const StyledChip = withStyles({
    root: {
      backgroundColor: status === 'active' ? '#D7C7F7' : '#BF6215',
      color: status === 'active' ? 'blueviolet' : 'white',
    },
  })(Chip);

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) {
        //setDirtyData(true);
        dispatch(setViewIsDirty(true));
      }

      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) {
        //setDirtyData(true);
        dispatch(setViewIsDirty(true));
      }
      return true;
    }
    return false;
  };

  const verifyLocName = (str) => {
    // verify length
    if (verifyLength(str, name, 3)) {
      // check for duplicate
      const loc = locations.find((location) => location.name === str);
      if (loc && str !== originalLocationName) {
        setNameError(
          'Duplicate Location Name already exists. Enter a different location name.'
        );
        return false;
      } else {
        setNameError('');
        return true;
      }
    } else {
      setNameError(
        'Field must contain at least 3 alphanumeric or special characters'
      );
      return false;
    }
  };

  const verifyLocId = (id) => {
    // verify length
    if (verifyLength(id, locId, 1)) {
      // check for duplicate
      const loc = locations.find((location) => location.key === id);
      if (loc && id !== originalLocationId) {
        setLocIdError(
          'Duplicate Location ID already exists. Enter a different location id.'
        );
        return false;
      } else {
        setLocIdError('');
        return true;
      }
    } else {
      setLocIdError(
        'Field must contain at least 1 alphanumeric or special characters'
      );
      return false;
    }
  };

  const verifySafe = (safe) => {
    // Any eligible device is a valid assignment - a safe-eligible cash drawer
    // is as good as a physical safe.
    if (isRegisteredSafe(safe)) return true;
    // Leaving the field empty is only acceptable while the location is still
    // pending - there is nothing to pick before devices are registered. Any
    // other status would persist the 'default' sentinel, which the API rejects
    // on read (isValidLocation / DEFAULT_SAFE_ID) and which leaves the
    // colleague app with no recovery route.
    if (newLocation || status === 'pending') return true;
    return false;
  };

  const verifyLocationType = (loctype) => {
    if (!loctype || loctype === '') return false;
    if (!locationTypes || !locationTypes.list) return false;
    if (locationTypes.list.find((lt) => lt.entry_code === loctype)) return true;
    return false;
  };

  const parameterChangeHandler = (event) => {
    if (event.target.value !== parameterSet) {
      const selectedSet = parameterSets.find(
        (set) => set.name === event.target.value
      );
      const endDate = selectedSet?.end_date
        ? new Date(selectedSet.end_date)
        : null;

      if (endDate && endDate < now) {
        setParameterSetError(
          'The selected parameter set has expired. Please select a valid set.'
        );
        setParameterSetState('error');
      } else {
        setParameterSetError('');
        setParameterSetState('success');
      }

      setParameterSet(event.target.value);
      dispatch(setViewIsDirty(true));
    }
  };

  const templateChangeHandler = (event) => {
    if (event.target.value !== templateSet) {
      const selectedTemplate = templateSets.find(
        (set) => set.name === event.target.value
      );
      const endDate = selectedTemplate?.end_date
        ? new Date(selectedTemplate.end_date)
        : null;

      if (endDate && endDate < now) {
        setTemplateSetError(
          'The selected template set has expired. Please select a valid set.'
        );
        setTemplateSetState('error');
      } else {
        setTemplateSetError('');
        setTemplateSetState('success');
      }

      setTemplateSet(event.target.value);
      dispatch(setViewIsDirty(true));
    }
  };

  const changeClick = async () => {
    if (
      isLocaleOutsideSupportedLanguages(
        locale,
        getTenantSupportedLanguages(tenant)
      )
    ) {
      setShowLocaleDriftAlert(true);
      return;
    }
    let error = false;
    let updatedLocation = cloneDeep(currentLocation);
    if (!verifyLocId(locId)) {
      error = true;
      setLocIdError(
        'Field must contain at least 1 alphanumeric or special characters'
      );
      setLocIdState('error');
    }
    if (!verifyLocName(name)) {
      error = true;
      setNameError(
        'Field must contain at least 3 alphanumeric or special characters'
      );
      setNameState('error');
    }
    if (!verifyLength(description, description, 3, false)) {
      error = true;
      setDescriptionState('error');
    }
    if (!verifyLocationType(locationType)) {
      error = true;
      setLocationTypeState('error');
    }
    if (!verifySafe(safe)) {
      error = true;
      setSafeState('error');
    }
    if ((!status || status === '') && statusState === '') {
      error = true;
      setStatusState('error');
    }
    if (!verifyLength(addressLine1, addressLine1, 3, false)) {
      error = true;
      setAddressLine1State('error');
    }
    if (!verifyLength(addressLine2, addressLine2, 3, false)) {
      error = true;
      setAddressLine2State('error');
    }
    if (!verifyLength(city, city, 3, false)) {
      error = true;
      setCityState('error');
    }
    if (!verifyLength(state, state, 2, false)) {
      error = true;
      setStateState('error');
    }
    if (!verifyLength(postalCode, postalCode, 3, false)) {
      error = true;
      setPostalCodeState('error');
    }
    if (!verifyLength(telephone, telephone, 7, false)) {
      error = true;
      setTelephoneState('error');
    }

    if (error === false) {
      updatedLocation.location_id = locId;
      updatedLocation.location_name = name;
      updatedLocation.description = description;
      updatedLocation.type = locationType;
      updatedLocation.card_payment_provider =
        currentLocation.cardPaymentProcessor ?? 'None';
      updatedLocation.card_processor_name =
        currentLocation.cardProcessorName ?? '';

      updatedLocation.gift_card_processor =
        currentLocation.giftCardProcessor ?? 'None';
      updatedLocation.gift_card_processor_name =
        currentLocation.giftCardProcessorName ?? '';

      updatedLocation.wallet_processor =
        currentLocation.walletProcessor ?? 'None';
      updatedLocation.wallet_processor_name =
        currentLocation.walletProcessorName ?? '';

      updatedLocation.poa_processor =
        currentLocation.payOnAccountProcessor ?? 'None';
      updatedLocation.poa_processor_name =
        currentLocation.payOnAccountProcessorName ?? '';

      // safeId is required to be non-empty by the API, so keep sending the
      // 'default' sentinel whenever nothing has been assigned yet.
      updatedLocation.safe_id = newLocation || !safe ? 'default' : safe;
      updatedLocation.status = status;
      updatedLocation.locale = locale;
      updatedLocation.parameters = parameterSet;
      updatedLocation.templates = templateSet;
      updatedLocation.address_line_1 = addressLine1;
      updatedLocation.address_line_2 = addressLine2;
      updatedLocation.city = city;
      updatedLocation.state = state;
      updatedLocation.postal_code = postalCode;
      updatedLocation.telephone_number = telephone;

      updatedLocation.tax_id = taxId;

      if (newLocation) await createLocation(updatedLocation).unwrap();
      else await updateLocation(updatedLocation).unwrap();

      pendingRedirectUrl = '/admin/locations';
      await dispatch(setViewIsDirty(false));
    }
  };

  const cancelClick = async () => {
    props.history.push('/admin/locations');
  };

  const statusChangeHandler = (event) => {
    if (event.target.value !== status) {
      setStatus(event.target.value);
      setStatusState('success');
      dispatch(setViewIsDirty(true));
    }
  };

  const typeChangeHandler = (event) => {
    if (event.target.value !== locationType) {
      setLocationType(event.target.value);
      setLocationTypeState('success');
      dispatch(setViewIsDirty(true));
    }
  };

  const safeChangeHandler = (event) => {
    if (event.target.value !== safe) {
      setSafe(event.target.value);
      setSafeState('success');
      dispatch(setViewIsDirty(true));
    }
  };

  const localeChangeHandler = (event) => {
    if (event.target.value !== locale) {
      setLocale(event.target.value);
      setLocaleState('success');
      dispatch(setViewIsDirty(true));
    }
  };

  const listOfLocationTypes = () => {
    if (!locationTypes || !locationTypes.list) return [];
    return locationTypes.list.map((loctype, index) => (
      <MenuItem key={index} value={loctype.entry_code}>
        {loctype.entry_label.find((label) => label.language === 'en-US').label}
      </MenuItem>
    ));
  };

  const listOfSafes = () => {
    return eligibleSafes.map((drawer, index) => (
      <MenuItem key={index} value={drawer.cashdrawer_id}>
        {drawer.description ? drawer.description : drawer.cashdrawer_id}
      </MenuItem>
    ));
  };

  const parameterSelection = () => {
    const seen = {};

    const parameterList = parameterSets
      .filter((set) => {
        return seen.hasOwnProperty(set.name) ? false : (seen[set.name] = true);
      })
      .map((set, key) => {
        const endDate = set.end_date ? new Date(set.end_date) : null;
        const startDate = set.start_date ? new Date(set.start_date) : null;
        const isExpired = endDate && endDate < now;
        const willExpireSoon =
          endDate && endDate <= next24Hours && endDate > now;

        let availableLabel = null;
        if (isExpired) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'red'}>
              (Expired)
            </font>
          );
        } else if (willExpireSoon) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'#f15f05'}>
              (Will expire soon)
            </font>
          );
        } else if (startDate && startDate > now) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'#0b8db4'}>
              (Available in future)
            </font>
          );
        }

        return (
          <MenuItem key={key} value={set.name}>
            {set.name} {availableLabel}
          </MenuItem>
        );
      });

    return (
      <>
        <Select
          variant='standard'
          className={classes.select}
          labelId='parameter-select-label'
          id='parameter-select'
          value={parameterSet}
          onChange={(event) => parameterChangeHandler(event)}
        >
          <MenuItem key={-1} value={'None'}>
            None
          </MenuItem>
          {parameterList}
        </Select>
        {parameterSetError && (
          <FormHelperText style={{ color: 'red' }}>
            {parameterSetError}
          </FormHelperText>
        )}
      </>
    );
  };

  const templateSelection = () => {
    const seen = {};
    const templateList = templateSets
      .filter((set) => {
        return seen.hasOwnProperty(set.name) ? false : (seen[set.name] = true);
      })
      .map((set, key) => {
        const endDate = set.end_date ? new Date(set.end_date) : null;
        const startDate = set.start_date ? new Date(set.start_date) : null;
        const isExpired = endDate && endDate < now;
        const willExpireSoon =
          endDate && endDate <= next24Hours && endDate > now;

        let availableLabel = null;
        if (isExpired) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'red'}>
              (Expired)
            </font>
          );
        } else if (willExpireSoon) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'#f15f05'}>
              (Will expire soon)
            </font>
          );
        } else if (startDate && startDate > now) {
          availableLabel = (
            <font className={classes.expirationLabels} color={'#0b8db4'}>
              (Available in future)
            </font>
          );
        }

        return (
          <MenuItem key={key} value={set.name}>
            {set.name} {availableLabel}
          </MenuItem>
        );
      });

    return (
      <>
        <Select
          variant='standard'
          className={classes.select}
          labelId='template-select-label'
          id='template-select'
          value={templateSet}
          onChange={(event) => templateChangeHandler(event)}
        >
          <MenuItem key={-1} value={'None'}>
            None
          </MenuItem>
          {templateList}
        </Select>
        {templateSetError && (
          <FormHelperText style={{ color: 'red' }}>
            {templateSetError}
          </FormHelperText>
        )}
      </>
    );
  };

  const baseLocaleSelection = () => {
    const languageList = envSupportedLanguages?.map((language, key) => {
      const option = (
        <MenuItem key={key} value={language}>
          {language}
        </MenuItem>
      );
      return option;
    });

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='locale-select-label'
        id='locale-select'
        value={locale}
        onChange={(event) => localeChangeHandler(event)}
      >
        {languageList}
      </Select>
    );
  };

  useEffect(() => {
    dispatch(setViewIsDirty(false));
  }, [dispatch]);

  useEffect(() => {
    if (newLocation || localeDriftPromptedRef.current) return;
    if (isLocaleOutsideSupportedLanguages(locale, getTenantSupportedLanguages(tenant))) {
      localeDriftPromptedRef.current = true;
      setShowLocaleDriftAlert(true);
    }
  }, [newLocation, locale, tenant]);

  // Re-reads the devices registered in the colleague app, bypassing the cache,
  // so newly registered safes / safe-eligible cash drawers show up without the
  // user having to refresh the browser.
  const refreshDevices = useCallback(async () => {
    if (!currentLocationId) return;
    try {
      const result = await getCashdrawers(currentLocationId, false).unwrap();
      setCashdrawers(result);
    } catch (error) {
      console.error('Error fetching cashdrawers:', error);
      setCashdrawers([]);
    }
    refetchWorkstations();
  }, [currentLocationId, getCashdrawers, refetchWorkstations]);

  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  // A stale validation error must not survive the data catching up with the
  // selection the user already made.
  useEffect(() => {
    if (safeState === 'error' && isRegisteredSafe(safe)) {
      setSafeState('');
    }
  }, [isRegisteredSafe, safe, safeState]);

  useEffect(() => {
    if (pendingRedirectUrl) {
      props.history.push(pendingRedirectUrl);
      pendingRedirectUrl = null;
    }
  }, [viewIsDirty, props.history]);

  const hasError = useCallback(() => {
    return (
      nameState === 'error' ||
      locIdState === 'error' ||
      descriptionState === 'error' ||
      locationTypeState === 'error' ||
      localeState === 'error' ||
      safeState === 'error' ||
      statusState === 'error' ||
      addressLine1State === 'error' ||
      addressLine2State === 'error' ||
      cityState === 'error' ||
      stateState === 'error' ||
      postalCodeState === 'error' ||
      telephoneState === 'error' ||
      parameterSetState === 'error' ||
      templateSetState === 'error' ||
      taxIdState === 'error'
    );
  }, [
    nameState,
    locIdState,
    descriptionState,
    locationTypeState,
    localeState,
    safeState,
    statusState,
    addressLine1State,
    addressLine2State,
    cityState,
    stateState,
    postalCodeState,
    telephoneState,
    parameterSetState,
    templateSetState,
    taxIdState,
  ]);

  // An unresolvable safe_id must not be handed to the Select as an
  // out-of-range value.
  const safeIsRegistered = isRegisteredSafe(safe);

  return (
    <GridContainer>
      {showLocaleDriftAlert && (
        <PromptAlert
          title='Language-Locale not supported'
          message="There are locations' Language-Locales that are not included in the Environment's allowed Language-Locales. Do you want to reset the Language-Locales to the Environment's default Locale?"
          okText='Reset to default'
          cancelText='Cancel'
          onConfirm={() => {
            setLocale(resolveEnvDefaultLocale(tenant, baseLocale));
            setLocaleState('success');
            dispatch(setViewIsDirty(true));
            setShowLocaleDriftAlert(false);
          }}
          onCancel={() => setShowLocaleDriftAlert(false)}
        />
      )}
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <LocationIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Location Information
              <StyledChip
                className={classes.chip}
                label={status === 'active' ? 'Active' : 'Pending'}
                color='primary'
              />
            </h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6}>
                <CustomInput
                  helperText={
                    nameState === 'error'
                      ? nameError ?? 'An error has occurred'
                      : false
                  }
                  success={nameState === 'success'}
                  error={nameState === 'error'}
                  labelText='Location Name'
                  id='name'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: name,
                    onChange: (event) => {
                      if (verifyLocName(event.target.value)) {
                        setNameState('success');
                      } else {
                        setNameState('error');
                      }
                      setName(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    locIdState === 'error'
                      ? locIdError ?? 'An error has occurred'
                      : false
                  }
                  success={locIdState === 'success'}
                  error={locIdState === 'error'}
                  labelText='Location ID'
                  id='locid'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    readOnly: !newLocation,
                    defaultValue: locId,
                    onChange: (event) => {
                      if (verifyLocId(event.target.value)) {
                        setLocIdState('success');
                      } else {
                        setLocIdState('error');
                      }
                      setLocId(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />

                <CustomInput
                  helperText={
                    taxIdState === 'error'
                      ? taxIdError ?? 'An error has occurred'
                      : false
                  }
                  success={taxIdState === 'success'}
                  error={taxIdState === 'error'}
                  labelText='Tax ID'
                  id='display'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    value: taxId ?? '',
                    onChange: (event) => {
                      if (verifyLength(event.target.value, taxId, 9, false)) {
                        setTaxIdState('success');
                        setTaxIdError('');
                      } else {
                        setTaxIdState('error');
                        setTaxIdError(
                          'Field must contain at least 9 alphanumeric or special characters'
                        );
                      }
                      setTaxId(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />

                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                  error={locationTypeState === 'error'}
                >
                  <InputLabel
                    id='type-select-label'
                    className={classes.inputLabel}
                  >
                    Location Type
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='type-select-label'
                    id='type-select'
                    value={locationType}
                    onChange={(event) => typeChangeHandler(event)}
                  >
                    {listOfLocationTypes()}
                  </Select>
                </FormControl>
                {!newLocation ? (
                  <FormControl
                    variant='standard'
                    className={classes.formControl}
                    fullWidth={true}
                  >
                    <InputLabel
                      id='status-select-label'
                      className={classes.inputLabel}
                    >
                      Status
                    </InputLabel>
                    <Select
                      variant='standard'
                      className={classes.select}
                      labelId='status-select-label'
                      id='status-select'
                      value={status}
                      onOpen={refreshDevices}
                      onChange={(event) => statusChangeHandler(event)}
                    >
                      {canLocationBeActivated() || status === 'active' ? (
                        <MenuItem value='active'>Active</MenuItem>
                      ) : null}
                      <MenuItem value='pending'>Pending</MenuItem>
                      <MenuItem value='inactive'>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                  error={safeState === 'error'}
                >
                  <InputLabel
                    id='safe-select-label'
                    className={classes.inputLabel}
                  >
                    Store Safe
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='safe-select-label'
                    id='safe-select'
                    value={safeIsRegistered ? safe : ''}
                    disabled={eligibleSafes.length === 0}
                    onOpen={refreshDevices}
                    onChange={(event) => safeChangeHandler(event)}
                  >
                    {/* Placeholder that keeps the value in range while nothing
                        is assigned; it drops away once a real option is picked. */}
                    {!safeIsRegistered ? (
                      <MenuItem value=''>&nbsp;</MenuItem>
                    ) : null}
                    {listOfSafes()}
                  </Select>
                  {eligibleSafes.length === 0 && (
                    <FormHelperText>
                      No safe or safe-eligible cash drawer has been registered
                      for this location yet.
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='parameter-select-label'
                    className={classes.inputLabel}
                  >
                    Parameter Set
                  </InputLabel>
                  {parameterSelection()}
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='template-select-label'
                    className={classes.inputLabel}
                  >
                    Template Set
                  </InputLabel>
                  {templateSelection()}
                </FormControl>
                <FormControl variant='standard' className={classes.formControl}>
                  <InputLabel
                    id='language-select-label'
                    className={classes.inputLabel}
                  >
                    Language
                  </InputLabel>
                  {baseLocaleSelection()}
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <CustomInput
                  helperText={
                    descriptionState === 'error'
                      ? descriptionError ?? 'An error has occurred'
                      : false
                  }
                  success={descriptionState === 'success'}
                  error={descriptionState === 'error'}
                  labelText='Location Description'
                  id='description'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: description,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, description, 3, false)
                      ) {
                        setDescriptionState('success');
                        setDescriptionError('');
                      } else {
                        setDescriptionState('error');
                        setDescriptionError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setDescription(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    addressLine1State === 'error'
                      ? addressLine1Error ?? 'An error has occurred'
                      : false
                  }
                  success={addressLine1State === 'success'}
                  error={addressLine1State === 'error'}
                  labelText='Address Line 1'
                  id='addressline1'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: addressLine1,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, addressLine1, 3, false)
                      ) {
                        setAddressLine1State('success');
                        setAddressLine1Error('');
                      } else {
                        setAddressLine1State('error');
                        setAddressLine1Error(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setAddressLine1(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    addressLine2State === 'error'
                      ? addressLine2Error ?? 'An error has occurred'
                      : false
                  }
                  success={addressLine2State === 'success'}
                  error={addressLine2State === 'error'}
                  labelText='Address Line 2'
                  id='addressline2'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: addressLine2,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, addressLine2, 3, false)
                      ) {
                        setAddressLine2State('success');
                        setAddressLine2Error('');
                      } else {
                        setAddressLine2State('error');
                        setAddressLine2Error(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setAddressLine2(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    cityState === 'error'
                      ? cityError ?? 'An error has occurred'
                      : false
                  }
                  success={cityState === 'success'}
                  error={cityState === 'error'}
                  labelText='City/Town'
                  id='city'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: city,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, city, 3, false)) {
                        setCityState('success');
                        setCityError('');
                      } else {
                        setCityState('error');
                        setCityError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setCity(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    stateState === 'error'
                      ? stateError ?? 'An error has occurred'
                      : false
                  }
                  success={stateState === 'success'}
                  error={stateState === 'error'}
                  labelText={country === 'US' ? 'State' : 'Province/County'}
                  id='state'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: state,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, state, 2, false)) {
                        setStateState('success');
                        setStateError('');
                      } else {
                        setStateState('error');
                        setStateError(
                          'Field must contain at least 2 alphanumeric or special characters'
                        );
                      }
                      setState(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    postalCodeState === 'error'
                      ? postalCodeError ?? 'An error has occurred'
                      : false
                  }
                  success={postalCodeState === 'success'}
                  error={postalCodeState === 'error'}
                  labelText='Postal Code'
                  id='postalcode'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: postalCode,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, postalCode, 3, false)
                      ) {
                        setPostalCodeState('success');
                        setPostalCodeError('');
                      } else {
                        setPostalCodeState('error');
                        setPostalCodeError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setPostalCode(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    telephoneState === 'error'
                      ? telephoneError ?? 'An error has occurred'
                      : false
                  }
                  success={telephoneState === 'success'}
                  error={telephoneState === 'error'}
                  labelText='Telephone Number'
                  id='telephone'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: telephone,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, telephone, 7, false)
                      ) {
                        setTelephoneState('success');
                        setTelephoneError('');
                      } else {
                        setTelephoneState('error');
                        setTelephoneError(
                          'Field must contain at least 7 alphanumeric or special characters'
                        );
                      }
                      setTelephone(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <Button
                  color='rose'
                  onClick={changeClick}
                  disabled={!viewIsDirty || hasError()}
                  className={classes.registerButton}
                >
                  Save Changes
                </Button>
                <Button
                  color='secondary'
                  onClick={cancelClick}
                  disabled={false}
                  className={classes.registerButton}
                >
                  Cancel
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
  //
}
